import {
  getRegisteredTabHost,
  isBackgroundIntent,
  openBackground,
  openForeground,
} from "@/helpers/appTabNavigation";

const INTERNAL_TABS_TEMP_DISABLED = true;

// Component-facing wrapper used by views/components. It prefers the shared broker-based
// navigation helpers and only falls back to legacy host discovery for older screens.
function hasTabApi(component) {
  return !!(
    component &&
    typeof component.upsertTab === "function" &&
    typeof component.shouldTrackRoute === "function" &&
    typeof component.persistOpenTabs === "function"
  );
}

function resolveTabHost(vm) {
  // Legacy fallback order:
  // 1. explicitly registered App.vue host
  // 2. walk parents for an embedded host
  // 3. inspect root children for older mounting shapes
  const registeredHost = getRegisteredTabHost();
  if (hasTabApi(registeredHost)) {
    return registeredHost;
  }

  let current = vm;
  while (current) {
    if (hasTabApi(current)) {
      return current;
    }
    current = current.$parent || null;
  }

  const rootChildren =
    vm &&
    vm.$root &&
    Array.isArray(vm.$root.$children) &&
    vm.$root.$children.length > 0
      ? vm.$root.$children
      : [];

  const appChild = rootChildren.find((child) => hasTabApi(child));
  return appChild || null;
}

function openInNativeBrowserTab(vm, routeLike) {
  if (!vm || !vm.$router) {
    return false;
  }

  const resolved = vm.$router.resolve(routeLike);
  const href = resolved && resolved.href ? resolved.href : null;
  if (!href) {
    return false;
  }

  window.open(href, "_blank", "noopener");
  return true;
}

function openWithLegacyFallback(vm, routeLike, { background = false } = {}) {
  if (!vm || !vm.$router) {
    return false;
  }

  const resolved = vm.$router.resolve(routeLike);
  const route = resolved && resolved.route ? resolved.route : null;
  const hasMatchedRoute =
    route && Array.isArray(route.matched) && route.matched.length > 0;
  if (!hasMatchedRoute) {
    return false;
  }

  if (!background) {
    vm.$router.push(route).catch(() => {});
    return true;
  }

  // Background fallback still honors shouldTrackRoute so login/2fa/forbidden never become tabs.
  const tabHost = resolveTabHost(vm);
  if (!tabHost || !tabHost.shouldTrackRoute(route)) {
    return false;
  }

  tabHost.upsertTab(route);
  tabHost.persistOpenTabs();
  return true;
}

export function isBackgroundOpen(event) {
  return isBackgroundIntent(event);
}

export function openInternal(vm, routeLike) {
  if (!vm || !vm.$router) {
    return false;
  }

  if (openForeground(vm.$router, routeLike)) {
    return true;
  }

  return openWithLegacyFallback(vm, routeLike, { background: false });
}

export function openInternalBackground(vm, routeLike) {
  if (!vm || !vm.$router) {
    return false;
  }

  // TEMP_INTERNAL_TABS_OFF_START
  if (INTERNAL_TABS_TEMP_DISABLED) {
    return openInNativeBrowserTab(vm, routeLike);
  }
  // TEMP_INTERNAL_TABS_OFF_END

  if (openBackground(vm.$router, routeLike)) {
    return true;
  }

  return openWithLegacyFallback(vm, routeLike, { background: true });
}

export function openInternalFromEvent(vm, routeLike, event) {
  // Centralizes the UX rule used across migrated screens:
  // normal click -> foreground, Ctrl/Cmd or middle click -> background tab.
  if (isBackgroundOpen(event)) {
    return openInternalBackground(vm, routeLike);
  }

  return openInternal(vm, routeLike);
}

export function openRouteInAppTab(vm, routeLike, { background = false } = {}) {
  if (background) {
    return openInternalBackground(vm, routeLike);
  }

  return openInternal(vm, routeLike);
}
